import React from 'react';
import { View, Text } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { Star, XCircle, Archive } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';

// ============================================================================
// Types
// ============================================================================

interface SubmissionActionBarProps {
  selectedCount: number;
  onBulkShortlist: () => void;
  onBulkReject: () => void;
  onBulkArchive: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function SubmissionActionBar({
  selectedCount,
  onBulkShortlist,
  onBulkReject,
  onBulkArchive,
}: SubmissionActionBarProps) {
  const colors = useThemeColors();

  return (
    <Animated.View
      entering={FadeInUp.duration(300)}
      className="absolute bottom-0 left-0 right-0 px-5 py-4"
      style={{
        backgroundColor: colors.primary[600],
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
      }}
    >
      <View className="flex-row items-center justify-between">
        <Text className="text-white font-medium">
          {selectedCount} submission{selectedCount > 1 ? 's' : ''} selected
        </Text>
        <View className="flex-row items-center gap-2">
          <AnimatedPressable
            onPress={onBulkArchive}
            accessibilityRole="button"
            accessibilityLabel={`Archive ${selectedCount} selected submissions`}
            className="px-3 py-2 rounded-lg flex-row items-center gap-1.5"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            <Archive size={16} color="white" />
            <Text className="text-white font-medium text-sm">Archive</Text>
          </AnimatedPressable>
          <AnimatedPressable
            onPress={onBulkReject}
            accessibilityRole="button"
            accessibilityLabel={`Reject ${selectedCount} selected submissions`}
            className="px-3 py-2 rounded-lg flex-row items-center gap-1.5"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            <XCircle size={16} color="white" />
            <Text className="text-white font-medium text-sm">Reject</Text>
          </AnimatedPressable>
          <AnimatedPressable
            onPress={onBulkShortlist}
            accessibilityRole="button"
            accessibilityLabel={`Shortlist ${selectedCount} selected submissions`}
            className="px-3 py-2 rounded-lg flex-row items-center gap-1.5"
            style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
          >
            <Star size={16} color="white" />
            <Text className="text-white font-medium text-sm">Shortlist</Text>
          </AnimatedPressable>
        </View>
      </View>
    </Animated.View>
  );
}
